import { Combobox, InputBase, Loader, NumberInput, Select, Text, TextInput, type useCombobox } from '@mantine/core';
import { CHURCH_TYPE } from '@/types/churches';
import { COUNTRIES_DATA } from '@/config/countriesData';
import { COORDINATE_FIELD_LIMITS, COORDINATE_TYPE } from '@/helpers';
import type { ClusterOrganizationEntry, OrganizationFormData } from './useOrganizationForm';
import type { useComboboxSearch } from './useComboboxSearch';
import styles from './EditOrganizationModal.module.sass';

const ORGANIZATION_TYPES = [
  { value: CHURCH_TYPE.PARISH, label: 'Parish' },
  { value: CHURCH_TYPE.DIOCESE, label: 'Diocese' },
];

const SHORT_NAME_MAX_LENGTH = 20;
const KEYWORD_MAX_LENGTH = 20;
const KEYWORD_MIN_LENGTH = 3;

const LAYOUT = {
  DROPDOWN_HEIGHT: 200,
};

type OrganizationBasicFieldsProps = {
  formData: OrganizationFormData;
  statesData: Array<{ value: string; label: string }>;
  stateSearch: string;
  setStateSearch: (value: string) => void;
  stateCombobox: ReturnType<typeof useCombobox>;
  isDiocese: boolean;
  dioceseSearch: ReturnType<typeof useComboboxSearch<unknown, { value: string; label: string }>>;
  latitudeError: string | null;
  longitudeError: string | null;
  onInputChange: (field: keyof OrganizationFormData, value: string | boolean | ClusterOrganizationEntry[]) => void;
  onCountryChange: (value: string) => void;
  onCoordinateChange: (params: { field: COORDINATE_TYPE; value: string | number | bigint }) => void;
};

export const OrganizationBasicFields = ({
  formData,
  statesData,
  stateSearch,
  setStateSearch,
  stateCombobox,
  isDiocese,
  dioceseSearch,
  latitudeError,
  longitudeError,
  onInputChange,
  onCountryChange,
  onCoordinateChange,
}: OrganizationBasicFieldsProps) => {
  return (
    <>
      <TextInput
        label="Full Name"
        placeholder="Full Name of Organization"
        value={formData.fullName}
        onChange={(e) => onInputChange('fullName', e.target.value)}
        required
      />

      <TextInput
        label="Short Name"
        placeholder="Short Name of Organization"
        value={formData.shortName}
        onChange={(e) => onInputChange('shortName', e.target.value)}
        maxLength={SHORT_NAME_MAX_LENGTH}
        required
      />

      <Select
        label="Type"
        placeholder="Type of Organization"
        data={ORGANIZATION_TYPES}
        value={formData.type}
        onChange={(value) => onInputChange('type', value || '')}
        required
      />

      <Select
        label="Country"
        placeholder="Country of Organization"
        data={COUNTRIES_DATA}
        value={formData.country}
        onChange={(value) => onCountryChange(value || 'US')}
        required
      />

      <TextInput
        label="Address 1"
        placeholder="Street address of Organization"
        value={formData.addressLine1}
        onChange={(e) => onInputChange('addressLine1', e.target.value)}
        required
      />

      <TextInput
        label="Address 2"
        placeholder="Additional address of Organization"
        value={formData.addressLine2}
        onChange={(e) => onInputChange('addressLine2', e.target.value)}
      />

      <TextInput
        label="City"
        placeholder="City of Organization"
        value={formData.city}
        onChange={(e) => onInputChange('city', e.target.value)}
        required
      />

      <Combobox
        store={stateCombobox}
        withinPortal={false}
        onOptionSubmit={(val) => {
          onInputChange('state', val);
          const selectedState = statesData.find((item) => item.value === val);
          setStateSearch(selectedState?.label || val);
          stateCombobox.closeDropdown();
        }}
      >
        <Combobox.Target>
          <InputBase
            label="State/Province"
            placeholder="Type to search states or enter custom value"
            rightSection={<Combobox.Chevron />}
            value={stateSearch}
            onChange={(event) => {
              stateCombobox.openDropdown();
              stateCombobox.updateSelectedOptionIndex();
              setStateSearch(event.currentTarget.value);
            }}
            onClick={() => stateCombobox.openDropdown()}
            onFocus={() => stateCombobox.openDropdown()}
            onBlur={() => {
              stateCombobox.closeDropdown();
              onInputChange('state', stateSearch);
            }}
            rightSectionPointerEvents="none"
            required
          />
        </Combobox.Target>

        <Combobox.Dropdown>
          <Combobox.Options mah={LAYOUT.DROPDOWN_HEIGHT} className={styles.dropdownOptions}>
            {statesData
              .filter((item) => item.label.toLowerCase().includes(stateSearch.toLowerCase().trim()))
              .map((item) => (
                <Combobox.Option value={item.label} key={item.value}>
                  {item.label}
                </Combobox.Option>
              ))}
          </Combobox.Options>
        </Combobox.Dropdown>
      </Combobox>

      <TextInput
        label="Zip"
        placeholder="Zip code of Organization"
        value={formData.zipCode}
        onChange={(e) => onInputChange('zipCode', e.target.value)}
        required
      />

      <NumberInput
        label="Latitude"
        placeholder="e.g. 42.88567890"
        description="Valid range: -90 to 90"
        value={formData.latitude}
        onChange={(value) => onCoordinateChange({ field: COORDINATE_TYPE.LATITUDE, value })}
        decimalScale={COORDINATE_FIELD_LIMITS[COORDINATE_TYPE.LATITUDE].decimalScale}
        allowNegative
        hideControls
        error={latitudeError}
      />

      <NumberInput
        label="Longitude"
        placeholder="e.g. -85.58765432"
        description="Valid range: -180 to 180"
        value={formData.longitude}
        onChange={(value) => onCoordinateChange({ field: COORDINATE_TYPE.LONGITUDE, value })}
        decimalScale={COORDINATE_FIELD_LIMITS[COORDINATE_TYPE.LONGITUDE].decimalScale}
        allowNegative
        hideControls
        error={longitudeError}
      />

      <TextInput
        label="Keyword"
        placeholder="Keyword"
        maxLength={KEYWORD_MAX_LENGTH}
        minLength={KEYWORD_MIN_LENGTH}
        value={formData.keyword}
        onChange={(e) => onInputChange('keyword', e.target.value)}
        required
      />
      {formData.keyword.length < KEYWORD_MIN_LENGTH && (
        <Text size="xs" c="red">
          Keyword must be at least {KEYWORD_MIN_LENGTH} characters
        </Text>
      )}

      {!isDiocese && (
        <Combobox
          store={dioceseSearch.combobox}
          onOptionSubmit={(value) => {
            onInputChange('dioceseId', value);
            dioceseSearch.setSearchTerm(dioceseSearch.results.find((item) => item.value === value)?.label || '');
            dioceseSearch.combobox.closeDropdown();
          }}
          withinPortal={false}
        >
          <Combobox.Target>
            <TextInput
              label="Diocese"
              placeholder="Type at least 3 characters to search dioceses"
              description="Search and select an existing diocese from the list"
              value={dioceseSearch.searchTerm}
              onChange={(event) => {
                dioceseSearch.handleSearch(event.currentTarget.value);
                onInputChange('dioceseId', '');
              }}
              onClick={() => dioceseSearch.combobox.openDropdown()}
              onBlur={() => dioceseSearch.combobox.closeDropdown()}
              rightSection={dioceseSearch.isLoading ? <Loader size="xs" /> : null}
              error={
                dioceseSearch.searchTerm.trim() && !formData.dioceseId
                  ? 'No matching diocese selected. Type to search, then pick a diocese from the dropdown'
                  : undefined
              }
              required
              disabled={dioceseSearch.isLoading}
            />
          </Combobox.Target>

          <Combobox.Dropdown hidden={dioceseSearch.results.length === 0 && !dioceseSearch.isLoading}>
            <Combobox.Options mah={LAYOUT.DROPDOWN_HEIGHT} className={styles.dropdownOptions}>
              {dioceseSearch.results.map((item) => (
                <Combobox.Option value={item.value} key={item.value}>
                  {item.label}
                </Combobox.Option>
              ))}
              {dioceseSearch.isEmpty && <Combobox.Empty>No dioceses found</Combobox.Empty>}
            </Combobox.Options>
          </Combobox.Dropdown>
        </Combobox>
      )}
    </>
  );
};
